import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SubPage, { PageAction, PageActions } from '#/common/components/SubPage';
import TenantSelector from '#/common/components/TenantSelector';
import { ImportFileOutputData, Tenant } from '#/graphql/types';
import useSession from '#/common/hooks/useSession';
import FileUpload from '#/common/components/FileUpload';
import MapMandatoryFieldsStep from '../components/MapMandatoryFieldsStep';
import styled from 'styled-components';
import TMMButton from '#/common/components/TMMButton';
import If from '#/common/components/If';
import MapAdditionalFieldsStep from '../components/MapAdditionalFieldsStep';
import PreviewStep from '../components/PreviewStep';
import { NuvoLabel } from '@nuvolo/nuux-core/components/NuvoLabel';
import { OptionInfo } from '#/common/components/ColumnMapper';
import { readFileLines } from '#/common/utils/fileStreamReader';
import WorkingOnItThrobber from '#/app/components/Throbbers/WorkingOnItThrobber';
import SubmitModal from '../components/SubmitModal';
import SuccessModal from '../components/SuccessModal';
import useToast from '#/common/hooks/useToast';
import { useNavigate } from 'react-router-dom';

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-bottom: 24px;
`;

const InsertThrobber = () => (
  <div
    style={{
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}>
    <WorkingOnItThrobber secondaryDescription="Please don't refresh or close this page" />
  </div>
);

enum ImportStep {
  FileUpload = 1,
  MapMandatory = 2,
  MapAdditional = 3,
  Preview = 4,
}

const initialTargetDropdownSelections: Record<string, string> = {
  asset_tag: 'asset_tag',
};

const NewDataAssetImportPage: React.FC = () => {
  const { alert } = useToast();
  const navigate = useNavigate();
  const { tenants: tenantsData } = useSession();
  const tenants = tenantsData?.map((t) => t.tenant) ?? [];
  const [selectedTenant, setSelectedTenant] = useState<Tenant | undefined>(
    tenants.length === 1 ? tenants[0] : undefined,
  );
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [currentStep, setCurrentStep] = useState<ImportStep>(ImportStep.FileUpload);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [insertingNewRecords, setInsertingNewRecords] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [importResult, setImportResult] = useState<ImportFileOutputData | null>(null);

  const [isStepMapMandatoryValid, setIsStepMapMandatoryValid] = useState<boolean>(false);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [csvColumns, setCsvColumns] = useState<OptionInfo[]>([]);
  const [csvTopLines, setCsvTopLines] = useState<string[][]>([]);

  // Save the state of dropdown selections to hide options that are already selected
  const [targetDropdownSelections, setTargetDropdownSelections] = useState<Record<string, string>>(
    initialTargetDropdownSelections,
  );

  const handleFileSelect = useCallback((file: File | null) => {
    setUploadedFile(file);
    // Reset states when a new file is uploaded
    setIsStepMapMandatoryValid(false);
    setMapping({});
    setCsvColumns([]);
    setCsvTopLines([]);
    setTargetDropdownSelections(initialTargetDropdownSelections);
  }, []);

  const handleNext = useCallback(() => {
    if (currentStep < ImportStep.Preview) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowSubmitModal(true);
    }
  }, [currentStep]);

  const handleBack = useCallback(() => {
    if (currentStep > ImportStep.FileUpload) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const stepLabel = useMemo(() => {
    return `Step ${currentStep}/${ImportStep.Preview}`;
  }, [currentStep]);

  const nextButtonLabel = useMemo(() => {
    if (currentStep < ImportStep.Preview) {
      return 'Next Step';
    } else {
      return 'Submit';
    }
  }, [currentStep]);

  // Parse CSV file to extract columns using streaming for efficiency
  useEffect(() => {
    if (!uploadedFile) return;

    // Extract only the headers (as OptionInfo) for column mapping
    const extractHeaders = async () => {
      const [firstLine] = await readFileLines(uploadedFile, 1);
      if (!firstLine) {
        setCsvColumns([]);
        return;
      }

      const headers = firstLine.split(',').map((h) => h.trim());
      if (headers.length === 0) return;

      // Generate column info with letters (A, B, C, etc.) and titles
      const columns: OptionInfo[] = headers.map((title, index) => {
        const letter = String.fromCharCode(65 + index); // 65 is 'A' in ASCII
        return {
          key: title,
          label: title ? `Column ${letter} (${title})` : `Column ${letter}`,
        } as OptionInfo;
      });

      setCsvColumns(columns);
    };

    // Extract the top 10 lines for preview
    const extractTopLines = async () => {
      const topLinesRaw = await readFileLines(uploadedFile, 11);
      if (topLinesRaw.length === 0) {
        setCsvTopLines([]);
        return;
      }

      const topLines: string[][] = topLinesRaw.map((line) => line.split(',').map((h) => h.trim()));
      setCsvTopLines(topLines);
    };

    extractHeaders();
    extractTopLines();
  }, [uploadedFile]);

  const disableNextButton = useMemo(() => {
    return (
      !selectedTenant ||
      !uploadedFile ||
      (currentStep === ImportStep.MapMandatory && !isStepMapMandatoryValid)
    );
  }, [selectedTenant, uploadedFile, currentStep, isStepMapMandatoryValid]);

  const actionsOnTop = useMemo<PageAction[]>(() => {
    return [
      {
        id: `step-back-on-top`,
        type: 'button',
        props: {
          type: 'normal',
          label: 'Back',
          onClick: handleBack,
          visible: currentStep > ImportStep.FileUpload,
        },
      },
      {
        id: `step-next-on-top`,
        type: 'button',
        props: {
          type: 'submit',
          label: nextButtonLabel,
          onClick: handleNext,
          disabled: disableNextButton,
        },
      },
    ];
  }, [handleBack, handleNext, nextButtonLabel, disableNextButton, currentStep]);

  const onSubmitSuccess = useCallback((importResult: ImportFileOutputData) => {
    setImportResult(importResult);
    setInsertingNewRecords(false);

    if (importResult.totalRows === importResult.successfulImports) {
      void alert(
        'success',
        `${importResult.successfulImports ?? 0} assets were imported successfully!`,
      );
      navigate('/assets/list');
    } else {
      setShowSuccessModal(true);
    }
  }, []);

  const onSubmitFailed = useCallback(() => {
    setImportResult(null);
    setInsertingNewRecords(false);
    void alert('error', 'Failed to import assets');
  }, []);

  const onSuccessModalHiding = () => {
    setShowSuccessModal(false);
    setImportResult(null);
  };

  return tenants.length > 1 && !selectedTenant ? (
    <TenantSelector onTenantSelect={setSelectedTenant} />
  ) : (
    <>
      {insertingNewRecords ? (
        <InsertThrobber />
      ) : (
        <SubPage title="New Data Asset Import" showGoBackButton>
          <PageActions actions={actionsOnTop} />
          <NuvoLabel>{stepLabel}</NuvoLabel>
          <If condition={currentStep === ImportStep.FileUpload}>
            <FileUpload
              uploadedFile={uploadedFile}
              onFileSelect={handleFileSelect}
              acceptedFormats={['.csv']}
            />
          </If>
          <If condition={currentStep === ImportStep.MapMandatory}>
            <MapMandatoryFieldsStep
              csvColumns={csvColumns}
              mapping={mapping}
              setMapping={setMapping}
              targetDropdownSelections={targetDropdownSelections}
              setTargetDropdownSelections={setTargetDropdownSelections}
              setIsValid={setIsStepMapMandatoryValid}
            />
          </If>
          <If condition={currentStep === ImportStep.MapAdditional}>
            <MapAdditionalFieldsStep
              csvColumns={csvColumns}
              mapping={mapping}
              setMapping={setMapping}
            />
          </If>
          <If condition={currentStep === ImportStep.Preview}>
            <PreviewStep mapping={mapping} csvTopLines={csvTopLines} />
          </If>
          <ButtonRow>
            <TMMButton
              type="normal"
              label="Back"
              onClick={handleBack}
              visible={currentStep > ImportStep.FileUpload}
            />
            <TMMButton
              type="submit"
              label={nextButtonLabel}
              onClick={handleNext}
              visible={currentStep > ImportStep.FileUpload}
              disabled={disableNextButton}
            />
          </ButtonRow>
        </SubPage>
      )}

      {/* Submit modal - choose whether to overwrite or skip existing data */}
      <SubmitModal
        visible={showSubmitModal}
        uploadedFile={uploadedFile}
        mapping={mapping}
        uniqueField={targetDropdownSelections['asset_tag']}
        selectedTenant={selectedTenant}
        onHiding={() => setShowSubmitModal(false)}
        onImportStart={() => setInsertingNewRecords(true)}
        onSuccess={onSubmitSuccess}
        onFailed={onSubmitFailed}
      />

      {/* Success modal - download file with invalid records or redirect to Asset List */}
      <SuccessModal
        visible={showSuccessModal}
        onHiding={onSuccessModalHiding}
        importResult={importResult}
      />
    </>
  );
};

export default React.memo(NewDataAssetImportPage);
